import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useClient } from "@app/portal-sdk";
import { Box, EmptyState, List, ListItem, ListItemButton, ListItemText, Stack, Typography, useMediaQuery } from "@app/ui-kit";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { settingsPath, settingsSections } from "./sections";

/**
 * SET-1: /c/:slug/settings. The sub-navigation is a vertical list on desktop
 * and a scrollable tab strip under 900px; the index (no sub-path) is what
 * teaches someone what is in here, so it is never a redirect.
 */
export const SettingsLayout = () => {
  const { client, canManage } = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const narrow = useMediaQuery("(max-width:900px)");
  const sections = settingsSections({ canManage });
  const base = settingsPath(client.slug);
  const current = sections.find((s) => location.pathname.startsWith(`${base}/${s.key}`))?.key ?? "";

  if (sections.length === 0) {
    return <EmptyState eyebrow="Settings" title="There is nothing here for you to change" description="A client administrator manages this client's settings." />;
  }

  const nav = narrow ? (
    <Tabs
      value={current}
      onChange={(_e, key: string) => navigate(key ? `${base}/${key}` : base)}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="Settings sections"
      sx={{ borderBottom: 1, borderColor: "divider" }}
    >
      <Tab value="" label="Overview" />
      {sections.map((s) => <Tab key={s.key} value={s.key} label={s.label} />)}
    </Tabs>
  ) : (
    <Box component="nav" aria-label="Settings sections" sx={{ width: 220, flexShrink: 0 }}>
      <Typography variant="overline" sx={{ color: "secondary.main", display: "block", px: 2, mb: 0.5 }}>Settings</Typography>
      <List dense disablePadding>
        <ListItem disablePadding>
          <ListItemButton selected={current === ""} onClick={() => navigate(base)} aria-current={current === "" ? "page" : undefined}>
            <ListItemText primary="Overview" />
          </ListItemButton>
        </ListItem>
        {sections.map((s) => (
          <ListItem key={s.key} disablePadding>
            <ListItemButton selected={current === s.key} onClick={() => navigate(`${base}/${s.key}`)} aria-current={current === s.key ? "page" : undefined}>
              <ListItemText primary={s.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Stack spacing={narrow ? 2 : 0} direction={narrow ? "column" : "row"} sx={{ gap: narrow ? 0 : 4, alignItems: "flex-start" }}>
      {nav}
      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <Outlet />
      </Box>
    </Stack>
  );
};
